/* eslint-disable max-lines-per-function */
import {toJS} from 'mobx'
import {Lang} from 'yougile-lang'
import {ArrUtil, Time, Util} from 'yougile-util'
import {
  createDataObjectId,
  DataObjectId,
  Hub,
  MovementTemplate,
  movementTemplatesContainerId,
  noBoardContainerId,
  RemoveAssignedOptions,
  SandboxTree
} from 'yougile-model'
import {SyncEvents} from '@client/sync-events'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'
import {Board} from '../board'
import {Project} from '../project'
import {Sticker} from '../sticker/sticker'
import {createTask} from './create-task'
import {StickersData} from './types'

export const createDeveloperBoard = (
  box: SandboxTree,
  project: Hub,
  stickers: StickersData
): void | {inProgressColId: DataObjectId; board: Hub} => {
  const board = Project.addBoard(Lang.msg.reg.boardDevTitle, project, false)
  if (!board) {
    return
  }

  const userId = globals.currentUserId
  const lang = Lang.msg.reg

  const col1 = Board.addColumn(Lang.msg.reg.boardDevCol1, board, false)
  col1.data ??= {}
  col1.data.columnColor = {hue: 8}
  col1.commit()

  const col2 = Board.addColumn(Lang.msg.reg.boardDevCol2, board, false)
  col2.data ??= {}
  col2.data.columnColor = {hue: 4}
  col2.commit()

  const col3 = Board.addColumn(Lang.msg.reg.boardDevCol3, board, false)
  col3.data ??= {}
  col3.data.columnColor = {hue: 5}
  col3.commit()

  board.data ??= {}
  board.data.settings = {
    autoDoneTaskWhenMoveToCol: {
      checked: false
    },
    moveTaskToColWhenDone: {
      checked: true,
      value: col3.id
    },
    counterInColumns: true,
    showTimeTaskInColumn: false,
    showIdOnTaskCard: false
  }
  board.data.stickersSettings ??= {}
  board.data.tabBgEnabled = true
  board.data.tabBgSkin = '-images/09-04'

  const {
    userSticker,
    deadlineTag,
    priorSticker,
    criPriorStickerStId,
    majPriorStickerStId,
    // normPriorStickerStId,
    // minPriorStickerStId,
    stopwatchSticker,
    sprintSticker,
    sprintStickerStId,
    taskTypeSticker,
    featureStId,
    bugStId,
    sectionSticker,
    deviceSticker,
    mobDeviceStickerStId,
    webDeviceStickerStId,
    mobWebDeviceStickerStId,
    estimationSticker
  } = stickers

  const {addToBoard} = Sticker

  /// Sticker: Исполнитель

  addToBoard(board, userSticker)

  /// Sticker: Дедлайн

  addToBoard(board, deadlineTag)

  /// Sticker: Приоритет

  addToBoard(board, priorSticker)

  /// Sticker: Stopwatch

  addToBoard(board, stopwatchSticker)
  board.data.stickersSettings[stopwatchSticker.id] = {
    shouldResetOnMove: true,
    shouldStopWhenCompleted: true
  }

  /// Sticker: Sprint

  addToBoard(board, sprintSticker)
  board.data.stickersSettings[sprintSticker.id] = {
    filterByCurrentValue: false
  }

  /// Sticker: Тип задачи

  addToBoard(board, taskTypeSticker)

  /// Sticker: Раздел

  addToBoard(board, sectionSticker)

  /// Sticker: Устройство

  addToBoard(board, deviceSticker, false)

  /// Sticker: Оценка в днях

  addToBoard(board, estimationSticker)
  board.data.stickersSettings[estimationSticker.id] = {
    showNumericStickersSum: true,
    titleBeforeValue: true
  }

  // col1 - backlog

  const task3Col1 = createTask(lang.boardDev.boardDevCol1Task3Title, col1.id)
  if (task3Col1) {
    Sticker.pin(taskTypeSticker, 'api', task3Col1, featureStId)
    Sticker.pin(deviceSticker, 'api', task3Col1, mobDeviceStickerStId)
    Sticker.pin(estimationSticker, 'api', task3Col1, '4')

    const task3Sub1Col1 = createTask(
      lang.boardDev.boardDevCol1Task3Sub1Title,
      noBoardContainerId
    )
    if (task3Sub1Col1) {
      task3Sub1Col1.data ??= {}
      task3Sub1Col1.data.color = 'task-yellow'
    }

    const hubs = ArrUtil.filterUndefined([task3Sub1Col1])

    const subTaskList = SubtaskUtil.addSubtasks({order: [], subtasks: {}}, hubs)
    task3Col1.data.subtaskList = toJS(subTaskList)
    task3Col1.commit()

    if (task3Sub1Col1) {
      Column.taskDropHandler(2, task3Sub1Col1, col2, {type: 'none'})
    }

    if (task3Sub1Col1) {
      task3Sub1Col1.commit()
    }
  }

  const task2Col1 = createTask(lang.boardDev.boardDevCol1Task2Title, col1.id)
  if (task2Col1) {
    Sticker.pin(priorSticker, 'api', task2Col1, majPriorStickerStId)
    Sticker.pin(estimationSticker, 'api', task2Col1, '1')

    Sticker.pin(deadlineTag, 'api', task2Col1, {
      current: {
        by: userId,
        deadline: 1705827600059,
        startDate: 1705654800059,
        order: 6,
        timestamp: 1705478677170,
        withTime: false,
        notifyBefore: 0
      },
      history: []
    })
  }

  const task1Col1 = createTask(lang.boardDev.boardDevCol1Task1Title, col1.id)
  if (task1Col1) {
    Sticker.pin(taskTypeSticker, 'api', task1Col1, featureStId)
    Sticker.pin(sectionSticker, 'api', task1Col1, 'дашборд')
    Sticker.pin(estimationSticker, 'api', task1Col1, '3')

    const task1Sub1Col1 = createTask(
      lang.boardDev.boardDevCol1Task1Sub1Title,
      noBoardContainerId
    )

    if (task1Sub1Col1) {
      Task.insertInGTD(
        task1Sub1Col1,
        'done',
        'createDeveloperBoard -> task1Sub1Col1'
      )
      Sticker.pin(sprintSticker, 'api', task1Sub1Col1, sprintStickerStId)
    }

    const task1Sub2Col1 = createTask(
      lang.boardDev.boardDevCol1Task1Sub2Title,
      noBoardContainerId
    )

    const task1Sub3Col1 = createTask(
      lang.boardDev.boardDevCol1Task1Sub3Title,
      noBoardContainerId
    )

    const hubs = ArrUtil.filterUndefined([
      task1Sub1Col1,
      task1Sub2Col1,
      task1Sub3Col1
    ])

    const subTaskList = SubtaskUtil.addSubtasks({order: [], subtasks: {}}, hubs)
    task1Col1.data.subtaskList = toJS(subTaskList)
    task1Col1.commit()
  }

  // col2 - in work

  const task1Col2 = createTask(lang.boardDev.boardDevCol2Task1Title, col2.id)
  if (task1Col2) {
    Sticker.pin(taskTypeSticker, 'api', task1Col2, bugStId)
    Sticker.pin(priorSticker, 'api', task1Col2, criPriorStickerStId)
    Sticker.pin(deadlineTag, 'api', task1Col2, {
      current: {
        deadline: 1702641599976,
        timestamp: 1703253985173,
        notifyBefore: 0,
        withTime: false,
        by: userId,
        startDate: 1702371600091
      },
      history: []
    })

    Sticker.pin(sprintSticker, 'api', task1Col2, sprintStickerStId)

    task1Col2.data ??= {}
    task1Col2.data.checklists = [
      {
        id: Util.UUID() as DataObjectId,
        title: 'Список',
        items: [
          {
            id: Util.UUID() as DataObjectId,
            isCompleted: true,
            title:
              'Проверка отображения специфических символов или форматирования в подсказках'
          },
          {
            id: Util.UUID() as DataObjectId,
            isCompleted: true,
            title:
              'Проверка отображения подсказки на различных разрешениях экрана'
          },
          {
            id: Util.UUID() as DataObjectId,
            isCompleted: true,
            title:
              'Проверка отображения всплывающей подсказки при наведении на элемент данных'
          }
        ]
      }
    ]
    task1Col2.commit()
  }

  const task3Col2 = createTask(lang.boardDev.boardDevCol2Task3Title, col2.id)
  if (task3Col2) {
    Sticker.pin(priorSticker, 'api', task3Col2, majPriorStickerStId)
    Sticker.pin(sprintSticker, 'api', task3Col2, sprintStickerStId)
    Sticker.pin(taskTypeSticker, 'api', task3Col2, featureStId)
    Sticker.pin(stopwatchSticker, 'api', task3Col2)

    const task3Sub1Col2 = createTask(
      lang.boardDev.boardDevCol2Task3Sub1Title,
      noBoardContainerId
    )
    if (task3Sub1Col2) {
      Task.insertInGTD(
        task3Sub1Col2,
        'done',
        'createDeveloperBoard -> task3Sub1Col2'
      )

      Sticker.pin(deadlineTag, 'api', task3Sub1Col2, {
        current: {
          deadline: 1704024000116,
          timestamp: 1703162868025,
          notifyBefore: 0,
          withTime: false,
          by: userId,
          startDate: 1703505600116
        },
        history: []
      })
    }

    const task3Sub2Col2 = createTask(
      lang.boardDev.boardDevCol2Task3Sub2Title,
      noBoardContainerId
    )
    if (task3Sub2Col2) {
      Task.insertInGTD(
        task3Sub2Col2,
        'done',
        'createDeveloperBoard -> task3Sub2Col2'
      )
    }

    const task3Sub3Col2 = createTask(
      lang.boardDev.boardDevCol2Task3Sub3Title,
      noBoardContainerId
    )

    const hubs = ArrUtil.filterUndefined([
      task3Sub1Col2,
      task3Sub2Col2,
      task3Sub3Col2
    ])

    const subTaskList = SubtaskUtil.addSubtasks({order: [], subtasks: {}}, hubs)
    task3Col2.data.subtaskList = toJS(subTaskList)
    task3Col2.commit()
  }

  // col3 - done

  setTimeout(() => {
    const task6Col3 = createTask(lang.boardDev.boardDevCol3Task6Title, col3.id)
    if (task6Col3) {
      Task.insertInGTD(task6Col3, 'done', 'createDeveloperBoard -> task6Col3')

      Sticker.pin(taskTypeSticker, 'api', task6Col3, bugStId)
      Sticker.pin(priorSticker, 'api', task6Col3, criPriorStickerStId)

      task6Col3.complete = Time.now()
      task6Col3.commit()
    }
  }, 500)

  setTimeout(() => {
    const task7Col3 = createTask(lang.boardDev.boardDevCol3Task7Title, col3.id)
    if (task7Col3) {
      Task.insertInGTD(task7Col3, 'done', 'createDeveloperBoard -> task7Col3')

      task7Col3.complete = Time.now()
      task7Col3.commit()
    }
  }, 300)

  setTimeout(() => {
    const task8Col3 = createTask(lang.boardDev.boardDevCol3Task8Title, col3.id)
    if (task8Col3) {
      Sticker.pin(taskTypeSticker, 'api', task8Col3, featureStId)
      Sticker.pin(sectionSticker, 'api', task8Col3, 'отчёты')

      Task.insertInGTD(task8Col3, 'done', 'createDeveloperBoard -> task8Col3')

      task8Col3.complete = Time.now()
      task8Col3.commit()
    }
  }, 0)

  //

  const task5Col3 = createTask(lang.boardDev.boardDevCol3Task5Title, col3.id)
  if (task5Col3) {
    Sticker.pin(taskTypeSticker, 'api', task5Col3, featureStId)
    Sticker.pin(deviceSticker, 'api', task5Col3, mobDeviceStickerStId)
    Task.insertInGTD(task5Col3, 'done', 'createDeveloperBoard -> task5Col3')
  }

  const task4Col3 = createTask(lang.boardDev.boardDevCol3Task4Title, col3.id)
  if (task4Col3) {
    Sticker.pin(taskTypeSticker, 'api', task4Col3, bugStId)
    Sticker.pin(priorSticker, 'api', task4Col3, criPriorStickerStId)
    Task.insertInGTD(task4Col3, 'done', 'createDeveloperBoard -> task4Col3')
  }

  const task3Col3 = createTask(lang.boardDev.boardDevCol3Task3Title, col3.id)
  if (task3Col3) {
    Sticker.pin(sectionSticker, 'api', task3Col3, 'отчёты')
    Sticker.pin(deviceSticker, 'api', task3Col3, webDeviceStickerStId)
    Task.insertInGTD(task3Col3, 'done', 'createDeveloperBoard -> task3Col3')
  }

  const task2Col3 = createTask(lang.boardDev.boardDevCol3Task2Title, col3.id)
  if (task2Col3) {
    task2Col3.data ??= {}
    const templatePath = '/img/first-proj/cover1.jpg'
    task2Col3.data.image = templatePath
    task2Col3.commit()
  }

  const task1Col3 = createTask(lang.boardDev.boardDevCol3Task1Title, col3.id)
  if (task1Col3) {
    Sticker.pin(sectionSticker, 'api', task1Col3, 'отчёты')
    Sticker.pin(deviceSticker, 'api', task1Col3, mobWebDeviceStickerStId)
    Sticker.pin(estimationSticker, 'api', task1Col3, '3')
    Task.insertInGTD(task1Col3, 'done', 'createDeveloperBoard -> task1Col3')
  }

  // add move template

  const moveTemplate = new Hub(box.companyId, '')
  moveTemplate.id = createDataObjectId()
  moveTemplate.title = moveTemplate.id
  moveTemplate.location = movementTemplatesContainerId

  const data: MovementTemplate = {
    id: moveTemplate.id,
    boardId: board.id,
    title: lang.boardDev.inProgressMoveTemplate,

    destinationProjectId: board.location,
    destinationBoardId: board.id,
    destinationColumnId: col2.id,
    moveFromColumns: [col1.id, col2.id, col3.id],

    enabled: true,
    moveFromColumnsAllIn: true,

    removeAssignedItem: undefined as unknown as RemoveAssignedOptions,
    removeAssignedUsers: [],
    addNewAssigned: [],

    unsubscribeItem: undefined as unknown as RemoveAssignedOptions,
    unsubscribeUsers: [],
    subscribeUsers: [],

    unpinStickers: [],
    pinStickers: {[sprintSticker.id]: sprintStickerStId}
  }
  moveTemplate.data.movementTemplate = data

  box.add(moveTemplate, moveTemplate.location, true, false)

  board.commit()

  setTimeout(() => {
    SyncEvents.trigger(box.companyId, [board.id], false)
  }, 1000)

  return {inProgressColId: col2.id, board}
}
